<template>
    <div v-if="campList !== null">
        <div class="control-panel d-flex">
            <template v-if="showMobileVersion">
                <div class="oval mobile-checked-btn">
                    <div v-if="!readonly && accessibleItems.length"
                         class="checkbox-wrapper">
                        <checkbox :transparent="true"
                                  :checked="checkedAll"
                                  v-select="campIds">
                        </checkbox>
                    </div>
                </div>
            </template>
            <template v-if="isMobile">
                <control-panel-mobile>
                    <div class="oval"
                         v-if="hasAccess(['email', 'camps.camps'])"
                         @click="openModal({ modalName: 'CampBulkEmailModal', options: { width: '680px' }  })">
                        <img src="../../../../assets/img/mail.svg">
                    </div>
                    <template v-if="env === 'development'">
                        <div class="oval"
                             v-if="hasAccess(['sms', 'camps.camps'])"
                             @click="openModal({ modalName: 'CampBulkSMSModal' })">
                            <img src="../../../../assets/img/sms.svg">
                        </div>
                    </template>
                    <div class="oval"
                         v-if="hasAccess(['download', 'camps.camps'])"
                         @click="openModal({ modalName: 'CampListBulkDownload' })">
                        <img src="../../../../assets/img/download.svg">
                    </div>
                    <div class="oval"
                         v-if="hasAccess(['remove', 'camps.camps'])"
                         @click="confirmDelete">
                        <img src="../../../../assets/img/delete.svg">
                    </div>
                </control-panel-mobile>
            </template>
            <template v-else>
                <template v-if="!readonly && selected && selected.length">
                    <div class="oval"
                         v-if="hasAccess(['email', 'camps.camps'])"
                         @click="openModal({ modalName: 'CampBulkEmailModal', options: { width: '680px' }  })">
                        <img src="../../../../assets/img/mail.svg">
                    </div>
                    <template v-if="env === 'development'">
                        <div class="oval"
                             v-if="hasAccess(['sms', 'camps.camps'])"
                             @click="openModal({ modalName: 'CampBulkSMSModal' })">
                            <img src="../../../../assets/img/sms.svg">
                        </div>
                    </template>
                    <div class="oval"
                         v-if="hasAccess(['download', 'camps.camps'])"
                         @click="openModal({ modalName: 'CampListBulkDownload' })">
                        <img src="../../../../assets/img/download.svg">
                    </div>
                    <div class="oval"
                         v-if="hasAccess(['remove', 'camps.camps'])"
                         @click="confirmDelete">
                        <img src="../../../../assets/img/delete.svg">
                    </div>
                </template>
            </template>
            <search-box @open-filter="openFilterModal"></search-box>
        </div>
        <template v-if="campList && campList.length">
            <div class="table-page">
                <template v-if="!showMobileVersion">
                    <div class="table-head">
                        <div class="table-row">
                            <div class="th"
                                 :style="{overflow: detectIE ? 'inherit' : 'unset'}">
                                <div v-if="!readonly && accessibleItems.length"
                                     class="checkbox-wrapper">
                                    <checkbox :transparent="true"
                                              :checked="checkedAll"
                                              v-select="campIds">
                                    </checkbox>
                                </div>
                            </div>
                            <div class="th" v-sort-by="'campName'">NAME</div>
                            <div class="th">DAYS</div>
                            <div class="th" v-sort-by>LOCATION</div>
                            <div class="th">DATES</div>
                            <div class="th"></div>
                        </div>
                    </div>
                    <div class="table-body">
                        <camp-row v-for="camp of campList"
                                  :key="camp.id"
                                  :readonly="readonly"
                                  :camp-id="camp.id"
                                  :camp-name="camp.campName"
                                  :location="camp.location"
                                  :days-list="daysList(camp)"
                                  :start-date="camp.campStartDate"
                                  :end-date="camp.campEndDate"
                                  :pricings="camp.pricings">
                        </camp-row>
                    </div>
                </template>
                <template v-else>
                    <div class="d-flex justify-content-center">
                        <div :class="[isMobile ? 'w-100' : 'w-50', 'mx-1']"
                             v-for="(cartColumn, index) of campsMatrixMobile"
                             :key="index">
                            <div class="mb-4"
                                 v-for="camp of cartColumn"
                                 :key="camp.id">
                                <camp-row :readonly="readonly"
                                          :camp-id="camp.id"
                                          :camp-name="camp.campName"
                                          :location="camp.location"
                                          :days-list="daysList(camp)"
                                          :start-date="camp.campStartDate"
                                          :end-date="camp.campEndDate"
                                          :pricings="camp.pricings">
                                </camp-row>
                            </div>
                        </div>
                    </div>
                    <pagination/>
                </template>
            </div>
            <pagination :page-sizes-array="PAGE_SIZES"></pagination>
        </template>
        <template v-else-if="!showProgress">
            <empty-page v-if="!searchQuery && !filters"
                        label="No camps added yet :|"
                        :logo-url="require('../../../../assets/img/camp_background.png')">
            </empty-page>
            <div class="not-found" v-else>
                Oh no! No camps found, please try again.
                <img src="../../../../assets/img/ilustra-empty-search.png">
            </div>
        </template>
    </div>
</template>

<script>
    import { mapActions, mapGetters } from 'vuex';
    import SelectDirective from '@/directives/SelectDirective';
    import SortByDirective from '@/directives/SortByDirective';
    import { PermissionMixin } from '@/mixins/PermissionMixin';
    import { ResizeMobileMixin } from '@/mixins/ResizeMobileMixin';
    import { Confirmation } from '@/store/modules/confirm/Confirmation';
    import { PAGE_SIZES } from '@/constants/pagination-page-size-increase';
    import { CAMP_OPTIONS } from './camp.options.config';

    const COLUMN_COUNT_RATIO = 430;
    const SIDEBAR_SIZE = 80;

    export default {
        name: 'ScheduleTab',
        components: {
            CampRow:     () => import(/* webpackChunkName: "camps" */ './CampRow'),
            Checkbox:    () => import('@/components/controls/Checkbox'),
            SearchBox:   () => import('@/components/controls/SearchBox'),
            DayPicker:   () => import('@/components/controls/DayPicker'),
            EmptyPage:   () => import('@/components/controls/EmptyPage'),
            Pagination:  () => import('@/components/controls/Pagination'),
            ControlPanelMobile: () => import('@/components/controls/ControlPanelMobile'),
        },
        props: {
            schoolId: {
                type: [String, Number],
                required: true
            },
            readonly: {
                type: Boolean,
                default: false
            }
        },
        data() {
            return {
                PAGE_SIZES,
                env: process.env.NODE_ENV
            }
        },
        computed: {
            ...mapGetters([
                'isMobile',
                'selected',
                'searchQuery',
                'checkedAll',
                'filters',
                'showProgress',
                'campList',
                'scheduleFilter',
                'subscriptions'
            ]),
            campIds() {
                return this.campList && this.campList.map(schoolCamp => schoolCamp.id);
            },
            detectIE() {
                return window.navigator.userAgent.indexOf('MSIE') > 0 || !!navigator.userAgent.match(/Trident.*rv\:11\./);
            },
            campsMatrixMobile() {
                const cartsMatrix = [];
                let columnCount;

                if (this.isMobile) {
                    columnCount = Math.floor(window.innerWidth / COLUMN_COUNT_RATIO);

                    if (!columnCount) {
                        columnCount = 1;
                    }

                } else {
                    columnCount = Math.round((window.innerWidth - SIDEBAR_SIZE) / COLUMN_COUNT_RATIO);
                }

                for (let i = 0; i < columnCount; i++) {
                    let column = [];

                    for (let j = i; j < this.campList.length; j += columnCount) {
                        column.push(this.campList[j]);
                    }

                    cartsMatrix.push(column);
                }

                return cartsMatrix;
            }
        },
        directives: { SelectDirective, SortByDirective },
        mixins: [PermissionMixin(CAMP_OPTIONS), ResizeMobileMixin(992)],
        methods: {
            ...mapActions([
                'getCampsList',
                'deleteMultipleCamps',
                'openConfirmDialog',
                'openModal',
                'createSuccessNotification',
                'filterElements'
            ]),
            openFilterModal() {
                this.openModal({
                    modalName: 'FilterModal',
                    options: {
                        filter: 'FilterActivitiesModal'
                    }
                })
            },
            daysList(camp) {
                return [camp.sunday, camp.monday, camp.tuesday, camp.wednesday,
                    camp.thursday, camp.friday, camp.saturday];
            },
            confirmDelete() {
                const deletedMoreThanOne = this.selected.length > 1;
                this.openConfirmDialog(new Confirmation(async () => {
                    await this.deleteMultipleCamps();
                    if (deletedMoreThanOne) {
                        this.createSuccessNotification('You’ve successfully deleted camps');
                    } else {
                        this.createSuccessNotification('You’ve successfully deleted a camp')
                    }
                }))
            }
        },
        created() {
            (this.scheduleFilter && !_.isEmpty(this.scheduleFilter) &&
                (!_.isEmpty(this.scheduleFilter.ages) || !_.isEmpty(this.scheduleFilter.grades) || !_.isEmpty(this.scheduleFilter.statuses) ||
                    this.scheduleFilter.dateFrom != null || this.scheduleFilter.dateTo != null ||
                    this.scheduleFilter.sunday || this.scheduleFilter.monday || this.scheduleFilter.tuesday ||
                    this.scheduleFilter.wednesday || this.scheduleFilter.thursday || this.scheduleFilter.friday ||
                    this.scheduleFilter.saturday)) && this.filterElements(this.scheduleFilter);
            const { schoolId } = this;
            this.getCampsList({ schoolId });
        }
    }
</script>

<style lang="scss" scoped>
    @import '../../../../assets/colors';
    @import '../../../../assets/mixins';

    .table-page {
        display: inline-table;

        /deep/ .td, /deep/ .th {
            &:first-child {
                flex-basis: 5%;
            }

            &:nth-child(2) {
                flex-basis: 18%;
            }

            &:nth-child(3) {
                flex-basis: 29%;
            }

            &:nth-child(4) {
                flex-basis: 19%;
            }

            &:nth-child(5) {
                flex-basis: 18%;
            }

            &:last-child {
                flex-basis: 10%;
                padding-right: 30px;
            }
        }

        @include table-body-mobile($minMax: 320px);
    }
</style>


// WEBPACK FOOTER //
// src/components/main/pages/camps/CampList.vue